// generated client
import {useMutation, useQuery} from "@tanstack/react-query";
import axios from "axios";
import {headers} from "./apiUtils";

declare var appConfig: any;

export interface ErrorResponse {
    code: string;
    message: string;
    details?: object;
}

export interface BusinessCompanyAddress {
    city: string;
    street: string;
    zip: string;
    country: string;
}

export interface DcBillingInfo {
    companyId: string;
    vatId?: string;
    companyName: string;
    address: BusinessCompanyAddress;
}

export interface DcDepartureInfo {
    requestedPickupAt: string;
    name: string;
    phoneNumber: string;
    email: string;
    passengerCount: number;
    luggageCount: number;
    childSeatCount: number;
    flightNumber?: string;
    noteForDriver?: string;
}

export interface DcOrder {
    carType?: "MAYBACH" | "FIRST_CLASS" | "BUSINESS_CLASS" | "BUSINESS_VAN";
    pickup?: Location;
    destination?: Location;
    specifiedDuration?: string;
    departureInfo?: DcDepartureInfo;
    billingInfo?: DcBillingInfo;
}

export interface Location {
    address: string;
    id?: string;
    type: "ADDRESS" | "PREDEFINED_PLACE";
    lat?: number;
    lon?: number;
}

export interface DcOrderResponse {
    order: DcOrder;
    price?: DcPrice;
    route?: DcRouteOverview;
}

export interface DcPrice {
    price: Money;
    maxDistance?: MaxDistance;
    priceIncludesExtraRouteToPickup?: boolean;
}

export interface DcRouteOverview {
    distanceKm: number;
    duration: string;
    supportedPickup: boolean;
}

export interface MaxDistance {
    overallKm: number;
    perHourKm: number;
    perDayKm: number;
}

export interface Money {
    amount: number;
    ccy: string;
}

export interface SetRouteChangeEnabled {
    enabled: boolean;
}

export interface BusinessPaymentType {
    paymentType: "CARD" | "INVOICE";
}

export interface BusinessPaymentInfoUpdate {
    projectCodeMode: "NONE" | "OPTIONAL" | "REQUIRED";
}

export interface BusinessPaymentInfoResponse {
    paymentType: "CARD" | "INVOICE";
    projectCodeMode: "NONE" | "OPTIONAL" | "REQUIRED";
    summaryPassword: string;
    paymentByCompanyForbidden: boolean;
    employeeExpenseLimitAllowed: boolean;
    paymentByEmployeeAllowed: boolean;
    routeChangeEnabled: boolean;
    invoicingDueDays?: number;
    payerId: string;
    cardDebt?: Money;
}

export interface UpdateBusinessCompanyRequest {
    displayName: string;
    companyName: string;
    companyId: string;
    taxId?: string;
    paysVAT: boolean;
    billingEmail?: string;
    billingAddress: BusinessCompanyAddress;
    contactEmails: string[];
    adminContact?: UpdateBusinessCompanyRequestAdminContact;
    adminId?: string;
    invoiceName?: string;
}

export interface UpdateBusinessCompanyRequestAdminContact {
    email?: string;
    phoneNumber?: string;
    name?: string;
}

export interface AdminContact {
    email: string;
    phoneNumber?: string;
    name?: string;
}

export interface BusinessCompany {
    businessId: string;
    displayName: string;
    companyName: string;
    companyId: string;
    taxId?: string;
    paysVAT: boolean;
    billingEmail?: string;
    billingAddress: BusinessCompanyAddress;
    contactEmails: string[];
    adminContact: AdminContact;
    adminId: string;
    invoiceName: string;
    companyNameLocked: boolean;
    cashPaymentPartPaidByCompany?: Money;
}

export interface UpdateBusinessUserRequest {
    name: string;
    phoneNumber: string;
    roles: ("BUSINESS_ADMIN" | "BUSINESS_ORDERER")[];
}

export interface CreateOrUpdateEmployeeRequest {
    phoneNumber: string;
    name: string;
    costCenter?: string;
    paymentOriginator: "COMPANY" | "EMPLOYEE";
    monthlyLimit?: Money;
    removeAfter?: string;
}

export interface PhoneValidationRequest {
    phoneNumber: string;
}

export interface ValidationResponse {
    valid: boolean;
}

export interface PhoneNumberValidationResponse {
    result: "VALID" | "INVALID" | "BANNED";
}

export interface FlightValidationRequest {
    flightNumber: string;
    requestedPickupAt: string;
}

export interface FlightValidationResponse {
    result: "VALID" | "NOT_FOUND" | "NOT_IN_FUTURE";
}

export interface DriverValidationRequest {
    driverId: string;
}

export interface BillingInfo {
    address: BusinessCompanyAddress;
    companyName: string;
    vatId?: string;
    companyId?: string;
}

export interface StopContact {
    name: string;
    phoneNumber: string;
}

export interface StopDefinition {
    location?: Location;
    requestedDuration?: string;
    type: "PICKUP" | "DROP_OFF";
    noteForDriver?: string;
    contact?: StopContact;
    suggestSessionId?: string;
}

export interface TaxiOrderRequest {
    orderUuid: string;
    publicHash?: string;
    stops: StopDefinition[];
    costCenter?: string;
    projectCode?: string;
    driverCallsOrderer: boolean;
    requestedPickupAt?: string;
    requestedDropOffAt?: string;
    flightNumber?: string;
    type: "PREORDER" | "SHORT_PREORDER" | "NORMAL";
    internalNote?: string;
    specifiedDriverId?: string;
    priceToken?: string;
    specifiedConsumerPrice?: Money;
    tierId: string;
    paymentMethod:
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL";
    publicOrderPaymentType?: "CARD" | "APPLE_PAY" | "GOOGLE_PAY" | "CASH";
    billingInfo?: BillingInfo;
    ordererEmail?: string;
}

export interface TaxiOrderResponse {
    orderId: string;
}

export interface PreOrderAvailabilityRequest {
    stops: StopDefinition[];
    tierGroup: "TAXI" | "ELITE";
}

export interface PreOrderAvailability {
    available: boolean;
    unavailabilityReason?:
        | "NOT_AVAILABLE"
        | "RIDE_TOO_SHORT"
        | "UNSUPPORTED_DESTINATION"
        | "UNSUPPORTED_TIER"
        | "UNSUPPORTED_TIME";
    minimalDistance?: number;
    earliestAllowedPreOrder?: string;
    latestAllowedPreOrder?: string;
    flightNumberAllowed?: boolean;
}

export interface PreOrderAvailabilityResponse {
    availabilities: TierPreOrderAvailability[];
}

export interface TierPreOrderAvailability {
    tierId: string;
    availability: PreOrderAvailability;
}

export interface TaxiOverviewRequest {
    stops: StopDefinition[];
    orderType: "PREORDER" | "SHORT_PREORDER" | "NORMAL";
    tierGroup: "TAXI" | "ELITE";
    requestedPickupAt?: string;
    requestedDropOffAt?: string;
    paymentMethod?:
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL";
    publicHash?: string;
}

export interface Price {
    final: PriceRange;
    beforeDiscount?: PriceRange;
}

export interface PriceRange {
    range: boolean;
    min: Money;
    max: Money;
}

export interface TaxiOverviewResponse {
    tiers: TaxiOverviewTierData[];
    distanceKm?: number;
    priceIncludesExtraRouteToPickup?: boolean;
}

export interface TaxiOverviewTierData {
    tierId: string;
    name: string;
    description: string;
    available: boolean;
    arrivalDuration?: string;
    rideDuration: string;
    legArrivals?: string[];
    rideShape?: string;
    minPrice: Money;
    maxPrice: Money;
    price: Price;
    preOrderAvailability: PreOrderAvailability;
    priceToken?: string;
    icon?: string;
}

export interface CreatePublicTaxiOrderLinkRequest {
    validFrom?: string;
    validUntil: string;
    fixedPickup?: Location;
    fixedDestination?: Location;
    maxUsagesPerPhoneNumber?: number;
    maxUsages?: number;
    maxAmount?: Money;
    paymentMethod:
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL";
    shortUrlEnding?: string;
}

export interface CreatePublicTaxiOrderLinkResponse {
    publicLink: string;
    shortUrl?: string;
    qrCodeBase64: string;
}

export interface PreOrderUpdate {
    requestedPickupAt: string;
    flightNumber?: string;
}

export interface PayTaxiTipRequest {
    tipPaymentId: string;
    price: Money;
    tipAmountType: "FIXED" | "PERCENT" | "CUSTOM";
}

export interface PayTaxiTipResponse {
    result: "SUCCESS" | "DENIED" | "ERROR";
}

export interface Coordinates {
    lat: number;
    lon: number;
}

export interface ReverseGeocodeRequest {
    coordinates: Coordinates;
}

export interface Contact {
    name: string;
    phoneNumber: string;
    company?: string;
}

export interface PriceEstimationRequest {
    stops: Stop[];
    deliveryType?: "ONE_WAY" | "ROUND_TRIP";
}

export interface Stop {
    location: Location;
    contact: Contact;
    kind: "PICKUP" | "HANDOVER" | "FALLBACK";
    noteForDriver?: string;
    arrivalTime?: TimeInterval;
    tripId?: string;
    suggestSessionId?: string;
}

export interface TimeInterval {
    from: string;
    to: string;
}

export interface PriceEstimationResponse {
    estimate: Money;
    priceToken?: string;
}

export interface BrowserJsData {
    language: string;
    colorDepth: number;
    screenHeight: number;
    screenWidth: number;
    timezone: number;
    javaEnabled: boolean;
}

export interface GooglePayProcessRequest {
    orderId: string;
    browserJsData: BrowserJsData;
}

export interface ActionsEndpointDefinition {
    url: string;
    method?: string;
    vars?: object;
}

export interface ProcessPaymentResult {
    outcome: "SUCCESS" | "FAILURE" | "ACTION_REQUIRED" | "CANCELLED";
    actionsEndpoint?: ActionsEndpointDefinition;
}

export interface GooglePayInitRequest {
    orderId: string;
    customerName: string;
    email?: string;
    phoneNumber?: string;
    redirectUri: string;
    paymentToken: string;
}

export interface GooglePayInitResponse {
    fingerPrintUri?: string;
}

export interface CardInitRequest {
    orderId: string;
    customerName: string;
    email?: string;
    phoneNumber?: string;
    language: string;
    redirectUri: string;
}

export interface CardInitResponse {
    processUri: string;
}

export interface ApplePayValidateSessionRequest {
    validationUri: string;
    language: string;
}

export interface ApplePayValidateSessionResponse {
    sessionData: string;
}

export interface ApplePayProcessOrderPaymentRequest {
    orderId: string;
    browserJsData: BrowserJsData;
}

export interface ApplePayInitOrderPaymentRequest {
    orderId: string;
    customerName: string;
    email?: string;
    phoneNumber?: string;
    redirectUri: string;
    paymentDataBase64: string;
}

export interface ApplePayInitOrderPaymentResponse {
    fingerPrintUri?: string;
}

export interface LocationSuggestionsRequest {
    query: string;
    addressType?: "PICKUP" | "DESTINATION";
    pickup?: Location;
    serviceType?: "TAXI" | "DELIVERY";
    suggestSessionId?: string;
    near?: Location;
    publicHash?: string;
}

export interface LocationSuggestion {
    location: Location;
    complete: boolean;
    type: "SEARCH" | "PREDEFINED_PLACE" | "HISTORY" | "POI";
    price?: Money;
}

export interface LocationSuggestionsResponse {
    suggestions: LocationSuggestion[];
}

export interface GeocodeRequest {
    query: string;
    near?: Coordinates;
}

export interface GeocodeResponse {
    id: string;
    coordinates: Coordinates;
}

export interface Feedback {
    text: string;
    currentUrl: string;
    type: "USER_INPUT" | "AUTOMATED";
}

export interface DcRouteOverviewRequest {
    pickup: Location;
    destination: Location;
}

export interface DcFinishOrderResponse {
    paymentLink: string;
}

export interface CarPriceOffer {
    price: Money;
    priceToken?: string;
    carType: "MAYBACH" | "FIRST_CLASS" | "BUSINESS_CLASS" | "BUSINESS_VAN";
}

export interface DcCarOverviewResponse {
    offers: CarPriceOffer[];
    maxDistance?: MaxDistance;
}

export interface CourierInfo {
    costCenter?: string;
    projectCode?: string;
    returnUndelivered: boolean;
    deliveryType?: "ONE_WAY" | "ROUND_TRIP";
    priceToken: string;
    tierId?: string;
}

export interface DeliveryOrderRequest {
    orderUuid?: string;
    stops: Stop[];
    courierInfo?: CourierInfo;
    specifiedDriverId?: string;
}

export interface DeliveryValidationRequest {
    stops: Location[];
}

export interface DeliveryValidationResponse {
    misplacedStopIndexes: number[];
}

export interface DeliveryArrivalEstimationRequest {
    stops: Stop[];
}

export interface RideEstimationResponse {
    estimatedArrival: string;
    estimatedRide: string;
}

export interface DeliveryOverviewRequest {
    stops: Stop[];
    serviceType: "ONE_WAY" | "ROUND_TRIP";
}

export interface CourierTiers {
    express?: DeliveryExpressTier;
    slotDelivery?: SlotDeliveryOverview;
}

export interface DeliveryExpressTier {
    tierId: string;
    priceOffer: PriceOffer;
    estimatedArrival?: string;
    estimatedRide?: string;
}

export interface DeliveryOverview {
    validFor: string;
    logistics?: PriceEstimationResponse;
    courier?: CourierTiers;
}

export interface DeliverySlotTier {
    tierId: string;
    slot: TimeInterval;
    priceOffer: PriceOffer;
    busy: boolean;
}

export interface PriceOffer {
    price: Money;
    token?: string;
}

export interface SlotDeliveryOverview {
    enabled: boolean;
    blockingStopIndices: number[];
    slots: DeliverySlotTier[];
}

export interface PtyAddressDto {
    city: string;
    street: string;
    zip: string;
    country: string;
}

export interface PtyAdminDto {
    email: string;
    name: string;
    phoneNumber: string;
    authService: string;
    secret: string;
}

export interface PtyCompanyDto {
    companyId: string;
    companyName: string;
    displayName: string;
    paysVAT: boolean;
    invoiceName?: string;
    taxId?: string;
    companyNameLocked?: boolean;
}

export interface PtyCreateBusinessRequest {
    admin: PtyAdminDto;
    billingAddress: PtyAddressDto;
    company: PtyCompanyDto;
    tenant?: "LIFTAGO" | "GRID" | "BOTH";
    referral?: string;
}

export interface CreateBusinessResult {
    businessId: string;
    accessToken: string;
}

export interface CreateBusinessUserRequest {
    email: string;
    name: string;
    phoneNumber: string;
    roles: ("BUSINESS_ADMIN" | "BUSINESS_ORDERER")[];
}

export interface BusinessUserDto {
    userId: string;
    email?: string;
    name?: string;
    phoneNumber?: string;
    roles: ("BUSINESS_ADMIN" | "BUSINESS_ORDERER")[];
}

export interface BusinessEmployeeDto {
    employeeId: string;
    phoneNumber: string;
    name: string;
    costCenter?: string;
    invitedAt: string;
    inviteAcceptedAt?: string;
    pasAppRegistered: boolean;
    paymentOriginator: "COMPANY" | "EMPLOYEE";
    monthlyLimit?: Money;
    removeAfter?: string;
}

export interface BusinessDebtResponse {
    cardDebt?: Money;
}

export interface InitPaymentCardEntryResponse {
    processUrl: string;
}

export interface LoginRequest {
    username: string;
    password: string;
}

export interface LoginResponse {
    accessToken: string;
    roles: (
        | "ADMIN"
        | "CUSTOMER_CARE"
        | "EXTERNAL_CUSTOMER_CARE"
        | "BUSINESS_ADMIN"
        | "BUSINESS_ORDERER"
        | "UNKNOWN"
    )[];
}

export interface ImpersonateBusinessRequest {
    businessId: string;
}

export interface ChangePasswordRequest {
    newPassword: string;
    changePasswordToken: string;
}

export interface AddOrdererRoleRequest {
    phoneNumber: string;
    name: string;
}

export interface AddOrdererRoleResponse {
    accessToken: string;
}

export interface RideUpdate {
    type: "INITIAL" | "TAXI" | "DELIVERY" | "ELITE";
}

export interface DriverInfo {
    id?: string;
    name: string;
    phoneNumber?: string;
    photoUrl?: string;
}

export interface PreOrderCancelFee {
    percent: number;
    appliesBeforeRequestedPickup: string;
}

export interface RidePrice {
    estimatedPrice: Money;
    partPaidByCompany?: Money;
    fixedPrice: boolean;
    tips?: RideTip[];
}

export interface RideTip {
    price: Money;
    type: "IN_RIDE" | "AFTER_RIDE" | "WEB_APP" | "IMPLICIT" | "PROMISED" | "UNKNOWN";
}

export interface SpecifiedDriverInfo {
    id: string;
    name: string;
    phoneNumber?: string;
}

export interface TaxiRide {
    id: string;
    createdAt: string;
    taxiOrderId: string;
    costCenter?: string;
    projectCode?: string;
    stops: TaxiStop[];
    cancellable: boolean;
    driver?: DriverInfo;
    vehicle?: Vehicle;
    pickupArrival?: string;
    status:
        | "SCHEDULING"
        | "SCHEDULED"
        | "AWAITING_PAYMENT"
        | "IN_SEARCHING"
        | "PICKUP_ARRIVAL"
        | "PICKUP_WAITING"
        | "ON_BOARD"
        | "RETURNING"
        | "FINISHED"
        | "CANCELLED"
        | "FAILED";
    statusGroup: "UPCOMING" | "IN_PROGRESS" | "COMPLETED";
    failReason?: "CANCELLED_BY_DRIVER" | "CANCELLED_BY_ORDERER" | "PAYMENT_AUTHORIZATION_FAILURE";
    finishedAt?: string;
    requestedPickupAt?: string;
    requestedDropOffAt?: string;
    orderType: "PREORDER" | "SHORT_PREORDER" | "NORMAL";
    internalNote?: string;
    ridePrice?: RidePrice;
    paymentMethod:
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL";
    requestedVehicle: "ANY" | "PREMIUM";
    shareUrl?: string;
    specifiedDriver?: SpecifiedDriverInfo;
    flightNumber?: string;
    tier: Tier;
    dcCarType?: "MAYBACH" | "FIRST_CLASS" | "BUSINESS_CLASS" | "BUSINESS_VAN";
    dcPrice?: DcPrice;
    routeChanged: boolean;
    tipEnabled: boolean;
    tipCcy?: string;
    tipped?: Money;
    paymentLink?: string;
    billingInfo?: BillingInfo;
    ordererEmail?: string;
    confirmationOfTransportUrl?: string;
    rideReceiptUrl?: string;
    preOrderCancelFee?: PreOrderCancelFee;
}

export interface TaxiStop {
    id: string;
    location?: Location;
    requestedDuration?: string;
    status:
        | "SCHEDULING"
        | "SCHEDULED"
        | "IN_PROGRESS"
        | "STORING"
        | "STORED"
        | "ARRIVED"
        | "COMPLETED"
        | "RETURNING"
        | "RETURNED"
        | "FAILED"
        | "CANCELLED";
    type: "PICKUP" | "DROP_OFF";
    noteForDriver?: string;
    contact?: StopContact;
    arrivalEstimate?: string;
    timeInfo?: TimeInfo;
}

export interface Tier {
    tierId: string;
    name: string;
    description: string;
}

export interface TimeInfo {
    title: string;
    subtitle?: string;
}

export interface Vehicle {
    licencePlate?: string;
    colorName?: string;
    model?: string;
    brand?: string;
}

export interface PublicTaxiOrderConfiguration {
    fixedPickup?: Location;
    fixedDestination?: Location;
    supportedPaymentMethods: (
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL"
    )[];
    callerIpLocation?: Location;
}

export interface DriverPosition {
    id: string;
    lat: number;
    lon: number;
    bearing: number;
}

export interface TaxiDriversPreviewResponse {
    validFor: string;
    drivers: DriverPosition[];
}

export interface PublicTaxiOrderLinkItem {
    id: string;
    publicLink: string;
    publicHash: string;
    shortUrl?: string;
    qrCodeBase64: string;
    validFrom?: string;
    validUntil: string;
    fixedPickup?: Location;
    fixedDestination?: Location;
    maxUsagesPerPhoneNumber?: number;
    maxUsages?: number;
    maxAmount?: Money;
    paymentMethod:
        | "COMPANY"
        | "PASSENGER_CASH"
        | "PASSENGER_CARD"
        | "EXTERNAL_CARD"
        | "PASSENGER_CASH_MISSING_CARD_TERMINAL";
}

export interface PublicTaxiOrderLinks {
    links: PublicTaxiOrderLinkItem[];
}

export interface TaxiRidesResponse {
    rides: TaxiRide[];
}

export interface DeliveryRide {
    id: string;
    order: OrderInfo;
    driver?: DriverInfo;
    vehicle?: Vehicle;
    status:
        | "SCHEDULING"
        | "SCHEDULED"
        | "AWAITING_PAYMENT"
        | "IN_SEARCHING"
        | "PICKUP_ARRIVAL"
        | "PICKUP_WAITING"
        | "ON_BOARD"
        | "RETURNING"
        | "FINISHED"
        | "CANCELLED"
        | "FAILED";
    statusGroup: "UPCOMING" | "IN_PROGRESS" | "COMPLETED";
    stops: DeliveryStop[];
    finishedAt?: string;
    shareUrl?: string;
    ridePrice?: RidePrice;
    tierId?: string;
}

export interface DeliveryRidesResponse {
    rides: DeliveryRide[];
}

export interface DeliveryStop {
    id: string;
    clientStopId: string;
    status:
        | "SCHEDULING"
        | "SCHEDULED"
        | "IN_PROGRESS"
        | "STORING"
        | "STORED"
        | "ARRIVED"
        | "COMPLETED"
        | "RETURNING"
        | "RETURNED"
        | "FAILED"
        | "CANCELLED";
    origin?: StopLocation;
    destination: StopLocation;
    contact: Contact;
    kind: "PICKUP" | "HANDOVER" | "FALLBACK";
    noteForDriver?: string;
    arrivalTime?: TimeInterval;
    arrival?: string;
    cancellable: boolean;
    courierInfo?: StopCourierInfo;
    depot?: Depot;
}

export interface Depot {
    reservationId: string;
    address: string;
    type: "ALZA_BOX";
}

export interface OrderInfo {
    createdAt: string;
    deliveryTime?: TimeInterval;
    cancellable: boolean;
}

export interface StopCourierInfo {
    costCenter?: string;
    projectCode?: string;
}

export interface StopLocation {
    description?: string;
    street: string;
    zipCode?: string;
    city: string;
    houseNumber: string;
    coordinates: Coordinates;
}

export interface CsobGooglePayInitParams {
    apiVersion: number;
    apiVersionMinor: number;
    paymentMethodType: string;
    allowedCardNetworks: string[];
    allowedCardAuthMethods: string[];
    assuranceDetailsRequired: boolean;
    billingAddressRequired: boolean;
    billingAddressParametersFormat: string;
    tokenizationSpecificationType: string;
    gateway: string;
    gatewayMerchantId: string;
    googlepayMerchantId: string;
    merchantName: string;
    environment: string;
    totalPriceStatus: string;
    countryCode: string;
}

export interface GooglePayInitParamsResponse {
    initParams: CsobGooglePayInitParams;
}

export interface ApplePayInitParams {
    countryCode: string;
    supportedNetworks: string[];
    merchantCapabilities: string[];
}

export interface ApplePayInitParamsResponse {
    displayName: string;
    initParams: ApplePayInitParams;
}

export interface ProjectCodeSettings {
    mode: "NONE" | "OPTIONAL" | "REQUIRED";
    codeHistory: string[];
}

export interface InternalNotes {
    internalNoteHistory: string[];
}

export interface CostCenters {
    costCenterHistory: string[];
}

export interface CompanyInfo {
    companyName: string;
    partnerDeliveryOrderEnabled: boolean;
    pricePerDropOff?: Money;
    priceEstimationAllowed: boolean;
    paymentByCompanyAllowed: boolean;
    horecaEnabled: boolean;
    optionalPassengerPhoneNumber: boolean;
    monthlyPaymentRequestedAt?: string;
    b2bOrderVisible: boolean;
    disabledInfo: DisabledInfo;
    tier: "BASIC" | "ADVANCED";
    cashPaymentPartPaidByCompany?: Money;
}

export interface DisabledInfo {
    ridesEnabled: boolean;
    disabled: boolean;
    hasCard: boolean;
    cardExpired: boolean;
}

export interface Employee {
    name: string;
    phoneNumber: string;
    costCenter?: string;
}

export interface Employees {
    employees: Employee[];
}

export interface CompanyLookupAddress {
    street: string;
    city: string;
    zip: string;
    country: string;
    state?: string;
}

export interface CompanyLookupCompany {
    companyName: string;
    companyId: string;
    taxId?: string;
    paysVAT: boolean;
}

export interface CompanyLookupResponse {
    found: boolean;
    company?: CompanyLookupCompany;
    billingAddress?: CompanyLookupAddress;
}

export interface CheckEmailResult {
    exists: boolean;
}

export interface CopmanyIdActiveResult {
    active: boolean;
}

export interface BusinessInvoicing {
    invoicingId: number;
    start: string;
    end: string;
    taxiRideCount?: number;
    numberOfShipments?: number;
    totalIssuedInvoicesAmount?: number;
    issuedInvoice?: IssuedInvoice;
}

export interface BusinessInvoicingList {
    items: BusinessInvoicing[];
}

export interface IssuedInvoice {
    invoiceId: number;
    amount: Money;
    paid: boolean;
    dueDate: string;
}

export interface CardInfo {
    id: number;
    maskedCln: string;
    expiration: string;
    type: string;
}

export interface UserInfo {
    email: string;
    roles: (
        | "ADMIN"
        | "CUSTOMER_CARE"
        | "EXTERNAL_CUSTOMER_CARE"
        | "BUSINESS_ADMIN"
        | "BUSINESS_ORDERER"
        | "UNKNOWN"
    )[];
    name?: string;
    phoneNumber?: string;
}

export interface AvailableBusiness {
    name: string;
    id: string;
}

export interface AvailableBusinessesResponse {
    supportsAll: boolean;
    businesses: AvailableBusiness[];
}

export interface SoftUpdateResponse {
    softUpdateNeeded: boolean;
}

export async function getOrderSession(): Promise<DcOrderResponse> {
    const response = await axios.get<DcOrderResponse>(
        `${appConfig.apiUrl}/self-service/driveczech/order`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetOrderSession() {
    return useQuery({
        queryKey: [`/self-service/driveczech/order`],
        queryFn: async () => await getOrderSession(),
    });
}

export async function putOrderSession(body: DcOrder): Promise<DcOrderResponse> {
    const response = await axios.put<DcOrderResponse>(
        `${appConfig.apiUrl}/self-service/driveczech/order`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function usePutOrderSession() {
    return useMutation({
        mutationKey: [`/self-service/driveczech/order`],
        mutationFn: async (body: DcOrder) => await putOrderSession(body),
    });
}

export async function setRouteChangeEnabled(body: SetRouteChangeEnabled) {
    const response = await axios.put(
        `${appConfig.apiUrl}/self-service/businesses/route-change-enabled`,
        body,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useSetRouteChangeEnabled() {
    return useMutation({
        mutationKey: [`/self-service/businesses/route-change-enabled`],
        mutationFn: async (body: SetRouteChangeEnabled) => await setRouteChangeEnabled(body),
    });
}

export async function setBusinessPaymentType(
    body: BusinessPaymentType
): Promise<BusinessPaymentType> {
    const response = await axios.put<BusinessPaymentType>(
        `${appConfig.apiUrl}/self-service/businesses/paymentType`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useSetBusinessPaymentType() {
    return useMutation({
        mutationKey: [`/self-service/businesses/paymentType`],
        mutationFn: async (body: BusinessPaymentType) => await setBusinessPaymentType(body),
    });
}

export async function getBusinessPaymentInfo(): Promise<BusinessPaymentInfoResponse> {
    const response = await axios.get<BusinessPaymentInfoResponse>(
        `${appConfig.apiUrl}/self-service/businesses/paymentInfo`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetBusinessPaymentInfo() {
    return useQuery({
        queryKey: [`/self-service/businesses/paymentInfo`],
        queryFn: async () => await getBusinessPaymentInfo(),
    });
}

export async function updateBusinessPaymentInfo(
    body: BusinessPaymentInfoUpdate
): Promise<BusinessPaymentInfoResponse> {
    const response = await axios.put<BusinessPaymentInfoResponse>(
        `${appConfig.apiUrl}/self-service/businesses/paymentInfo`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useUpdateBusinessPaymentInfo() {
    return useMutation({
        mutationKey: [`/self-service/businesses/paymentInfo`],
        mutationFn: async (body: BusinessPaymentInfoUpdate) =>
            await updateBusinessPaymentInfo(body),
    });
}

export async function getBusinessCompany(): Promise<BusinessCompany> {
    const response = await axios.get<BusinessCompany>(
        `${appConfig.apiUrl}/self-service/businesses/company`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetBusinessCompany() {
    return useQuery({
        queryKey: [`/self-service/businesses/company`],
        queryFn: async () => await getBusinessCompany(),
    });
}

export async function updateBusinessCompany(
    body: UpdateBusinessCompanyRequest
): Promise<BusinessCompany> {
    const response = await axios.put<BusinessCompany>(
        `${appConfig.apiUrl}/self-service/businesses/company`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useUpdateBusinessCompany() {
    return useMutation({
        mutationKey: [`/self-service/businesses/company`],
        mutationFn: async (body: UpdateBusinessCompanyRequest) => await updateBusinessCompany(body),
    });
}

export async function updateUser(body: UpdateBusinessUserRequest, userId: string) {
    const response = await axios.put(
        `${appConfig.apiUrl}/self-service/business/users/${encodeURIComponent(userId)}`,
        body,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useUpdateUser() {
    return useMutation({
        mutationKey: [`/self-service/business/users/{userId}`],
        mutationFn: async ({body, userId}: {body: UpdateBusinessUserRequest; userId: string}) =>
            await updateUser(body, userId),
    });
}

export async function removeUserFromBusiness(userId: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/business/users/${encodeURIComponent(userId)}`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useRemoveUserFromBusiness() {
    return useMutation({
        mutationKey: [`/self-service/business/users/{userId}`],
        mutationFn: async (userId: string) => await removeUserFromBusiness(userId),
    });
}

export async function updateEmployee(body: CreateOrUpdateEmployeeRequest, employeeId: string) {
    const response = await axios.put(
        `${appConfig.apiUrl}/self-service/business/employees/${encodeURIComponent(employeeId)}`,
        body,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useUpdateEmployee() {
    return useMutation({
        mutationKey: [`/self-service/business/employees/{employeeId}`],
        mutationFn: async ({
            body,
            employeeId,
        }: {
            body: CreateOrUpdateEmployeeRequest;
            employeeId: string;
        }) => await updateEmployee(body, employeeId),
    });
}

export async function deleteEmployee(employeeId: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/business/employees/${encodeURIComponent(employeeId)}`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useDeleteEmployee() {
    return useMutation({
        mutationKey: [`/self-service/business/employees/{employeeId}`],
        mutationFn: async (employeeId: string) => await deleteEmployee(employeeId),
    });
}

export async function phoneNumberValid(body: PhoneValidationRequest): Promise<ValidationResponse> {
    const response = await axios.post<ValidationResponse>(
        `${appConfig.apiUrl}/self-service/validations/phoneNumber`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function usePhoneNumberValid() {
    return useMutation({
        mutationKey: [`/self-service/validations/phoneNumber`],
        mutationFn: async (body: PhoneValidationRequest) => await phoneNumberValid(body),
    });
}

export async function validatePhoneNumber(
    body: PhoneValidationRequest
): Promise<PhoneNumberValidationResponse> {
    const response = await axios.post<PhoneNumberValidationResponse>(
        `${appConfig.apiUrl}/self-service/validations/phone-number`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useValidatePhoneNumber() {
    return useMutation({
        mutationKey: [`/self-service/validations/phone-number`],
        mutationFn: async (body: PhoneValidationRequest) => await validatePhoneNumber(body),
    });
}

export async function validateFlight(
    body: FlightValidationRequest
): Promise<FlightValidationResponse> {
    const response = await axios.post<FlightValidationResponse>(
        `${appConfig.apiUrl}/self-service/validations/flight`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useValidateFlight() {
    return useMutation({
        mutationKey: [`/self-service/validations/flight`],
        mutationFn: async (body: FlightValidationRequest) => await validateFlight(body),
    });
}

export async function validateDriver(body: DriverValidationRequest): Promise<ValidationResponse> {
    const response = await axios.post<ValidationResponse>(
        `${appConfig.apiUrl}/self-service/validations/driver`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useValidateDriver() {
    return useMutation({
        mutationKey: [`/self-service/validations/driver`],
        mutationFn: async (body: DriverValidationRequest) => await validateDriver(body),
    });
}

export async function createTaxiOrder(body: TaxiOrderRequest) {
    const response = await axios.post(`${appConfig.apiUrl}/self-service/taxiOrder`, body, {
        headers: headers(),
    });

    return response;
}

export function useCreateTaxiOrder() {
    return useMutation({
        mutationKey: [`/self-service/taxiOrder`],
        mutationFn: async (body: TaxiOrderRequest) => await createTaxiOrder(body),
    });
}

export async function preOrderAvailabilityV2(
    body: PreOrderAvailabilityRequest
): Promise<PreOrderAvailabilityResponse> {
    const response = await axios.post<PreOrderAvailabilityResponse>(
        `${appConfig.apiUrl}/self-service/taxiOrder/preOrderAvailabilityV2`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function usePreOrderAvailabilityV2() {
    return useMutation({
        mutationKey: [`/self-service/taxiOrder/preOrderAvailabilityV2`],
        mutationFn: async (body: PreOrderAvailabilityRequest) => await preOrderAvailabilityV2(body),
    });
}

export async function taxiOverview(body: TaxiOverviewRequest): Promise<TaxiOverviewResponse> {
    const response = await axios.post<TaxiOverviewResponse>(
        `${appConfig.apiUrl}/self-service/taxiOrder/overview`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useTaxiOverview() {
    return useMutation({
        mutationKey: [`/self-service/taxiOrder/overview`],
        mutationFn: async (body: TaxiOverviewRequest) => await taxiOverview(body),
    });
}

export async function listPublicTaxiOrderLinks(): Promise<PublicTaxiOrderLinks> {
    const response = await axios.get<PublicTaxiOrderLinks>(
        `${appConfig.apiUrl}/self-service/taxi/public-order/links`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useListPublicTaxiOrderLinks() {
    return useQuery({
        queryKey: [`/self-service/taxi/public-order/links`],
        queryFn: async () => await listPublicTaxiOrderLinks(),
    });
}

export async function createPublicTaxiOrderLink(
    body: CreatePublicTaxiOrderLinkRequest
): Promise<CreatePublicTaxiOrderLinkResponse> {
    const response = await axios.post<CreatePublicTaxiOrderLinkResponse>(
        `${appConfig.apiUrl}/self-service/taxi/public-order/links`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCreatePublicTaxiOrderLink() {
    return useMutation({
        mutationKey: [`/self-service/taxi/public-order/links`],
        mutationFn: async (body: CreatePublicTaxiOrderLinkRequest) =>
            await createPublicTaxiOrderLink(body),
    });
}

export async function deactivatePublicTaxiOrderLink(publicHash: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/taxi/public-order/links`,
        {
            headers: headers(),
            params: {publicHash},
        }
    );

    return response;
}

export function useDeactivatePublicTaxiOrderLink() {
    return useMutation({
        mutationKey: [`/self-service/taxi/public-order/links`],
        mutationFn: async (publicHash: string) => await deactivatePublicTaxiOrderLink(publicHash),
    });
}

export async function updatePreorder(body: PreOrderUpdate, rideId: string) {
    const response = await axios.post(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}/update-preorder`,
        body,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useUpdatePreorder() {
    return useMutation({
        mutationKey: [`/self-service/rides/{rideId}/update-preorder`],
        mutationFn: async ({body, rideId}: {body: PreOrderUpdate; rideId: string}) =>
            await updatePreorder(body, rideId),
    });
}

export async function payTaxiTip(
    body: PayTaxiTipRequest,
    rideId: string
): Promise<PayTaxiTipResponse> {
    const response = await axios.post<PayTaxiTipResponse>(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}/taxi/tip`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function usePayTaxiTip() {
    return useMutation({
        mutationKey: [`/self-service/rides/{rideId}/taxi/tip`],
        mutationFn: async ({body, rideId}: {body: PayTaxiTipRequest; rideId: string}) =>
            await payTaxiTip(body, rideId),
    });
}

export async function reverseGeocode(body: ReverseGeocodeRequest): Promise<Location> {
    const response = await axios.post<Location>(
        `${appConfig.apiUrl}/self-service/reverse-geocode`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useReverseGeocode() {
    return useMutation({
        mutationKey: [`/self-service/reverse-geocode`],
        mutationFn: async (body: ReverseGeocodeRequest) => await reverseGeocode(body),
    });
}

export async function estimate(body: PriceEstimationRequest): Promise<PriceEstimationResponse> {
    const response = await axios.post<PriceEstimationResponse>(
        `${appConfig.apiUrl}/self-service/pricing/estimate`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useEstimate() {
    return useMutation({
        mutationKey: [`/self-service/pricing/estimate`],
        mutationFn: async (body: PriceEstimationRequest) => await estimate(body),
    });
}

export async function googlePayProcess(
    body: GooglePayProcessRequest
): Promise<ProcessPaymentResult> {
    const response = await axios.post<ProcessPaymentResult>(
        `${appConfig.apiUrl}/self-service/payment/google/process`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGooglePayProcess() {
    return useMutation({
        mutationKey: [`/self-service/payment/google/process`],
        mutationFn: async (body: GooglePayProcessRequest) => await googlePayProcess(body),
    });
}

export async function googlePayInit(body: GooglePayInitRequest): Promise<GooglePayInitResponse> {
    const response = await axios.post<GooglePayInitResponse>(
        `${appConfig.apiUrl}/self-service/payment/google/init`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGooglePayInit() {
    return useMutation({
        mutationKey: [`/self-service/payment/google/init`],
        mutationFn: async (body: GooglePayInitRequest) => await googlePayInit(body),
    });
}

export async function payByCard(body: CardInitRequest): Promise<CardInitResponse> {
    const response = await axios.post<CardInitResponse>(
        `${appConfig.apiUrl}/self-service/payment/card`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function usePayByCard() {
    return useMutation({
        mutationKey: [`/self-service/payment/card`],
        mutationFn: async (body: CardInitRequest) => await payByCard(body),
    });
}

export async function validateSession(
    body: ApplePayValidateSessionRequest
): Promise<ApplePayValidateSessionResponse> {
    const response = await axios.post<ApplePayValidateSessionResponse>(
        `${appConfig.apiUrl}/self-service/payment/applepay/validateSession`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useValidateSession() {
    return useMutation({
        mutationKey: [`/self-service/payment/applepay/validateSession`],
        mutationFn: async (body: ApplePayValidateSessionRequest) => await validateSession(body),
    });
}

export async function processPayment(
    body: ApplePayProcessOrderPaymentRequest
): Promise<ProcessPaymentResult> {
    const response = await axios.post<ProcessPaymentResult>(
        `${appConfig.apiUrl}/self-service/payment/applepay/process`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useProcessPayment() {
    return useMutation({
        mutationKey: [`/self-service/payment/applepay/process`],
        mutationFn: async (body: ApplePayProcessOrderPaymentRequest) => await processPayment(body),
    });
}

export async function initializePayment(
    body: ApplePayInitOrderPaymentRequest
): Promise<ApplePayInitOrderPaymentResponse> {
    const response = await axios.post<ApplePayInitOrderPaymentResponse>(
        `${appConfig.apiUrl}/self-service/payment/applepay/init`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useInitializePayment() {
    return useMutation({
        mutationKey: [`/self-service/payment/applepay/init`],
        mutationFn: async (body: ApplePayInitOrderPaymentRequest) => await initializePayment(body),
    });
}

export async function getLocationSuggestions(
    body: LocationSuggestionsRequest
): Promise<LocationSuggestionsResponse> {
    const response = await axios.post<LocationSuggestionsResponse>(
        `${appConfig.apiUrl}/self-service/location-suggestions`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetLocationSuggestions() {
    return useMutation({
        mutationKey: [`/self-service/location-suggestions`],
        mutationFn: async (body: LocationSuggestionsRequest) => await getLocationSuggestions(body),
    });
}

export async function geocode(body: GeocodeRequest): Promise<GeocodeResponse> {
    const response = await axios.post<GeocodeResponse>(
        `${appConfig.apiUrl}/self-service/geocode`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGeocode() {
    return useMutation({
        mutationKey: [`/self-service/geocode`],
        mutationFn: async (body: GeocodeRequest) => await geocode(body),
    });
}

export async function sendFeedback(body: Feedback) {
    const response = await axios.post(`${appConfig.apiUrl}/self-service/feedback`, body, {
        headers: headers(),
    });

    return response;
}

export function useSendFeedback() {
    return useMutation({
        mutationKey: [`/self-service/feedback`],
        mutationFn: async (body: Feedback) => await sendFeedback(body),
    });
}

export async function routeOverview(body: DcRouteOverviewRequest): Promise<DcRouteOverview> {
    const response = await axios.post<DcRouteOverview>(
        `${appConfig.apiUrl}/self-service/driveczech/route-overview`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useRouteOverview() {
    return useMutation({
        mutationKey: [`/self-service/driveczech/route-overview`],
        mutationFn: async (body: DcRouteOverviewRequest) => await routeOverview(body),
    });
}

export async function finishOrderSession() {
    const response = await axios.post(
        `${appConfig.apiUrl}/self-service/driveczech/order/finish`,
        null,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useFinishOrderSession() {
    return useMutation({
        mutationKey: [`/self-service/driveczech/order/finish`],
        mutationFn: async () => await finishOrderSession(),
    });
}

export async function carOverview(): Promise<DcCarOverviewResponse> {
    const response = await axios.post<DcCarOverviewResponse>(
        `${appConfig.apiUrl}/self-service/driveczech/car-overview`,
        null,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCarOverview() {
    return useMutation({
        mutationKey: [`/self-service/driveczech/car-overview`],
        mutationFn: async () => await carOverview(),
    });
}

export async function createDeliveryOrder(body: DeliveryOrderRequest) {
    const response = await axios.post(`${appConfig.apiUrl}/self-service/deliveryOrder`, body, {
        headers: headers(),
    });

    return response;
}

export function useCreateDeliveryOrder() {
    return useMutation({
        mutationKey: [`/self-service/deliveryOrder`],
        mutationFn: async (body: DeliveryOrderRequest) => await createDeliveryOrder(body),
    });
}

export async function validateStops(
    body: DeliveryValidationRequest
): Promise<DeliveryValidationResponse> {
    const response = await axios.post<DeliveryValidationResponse>(
        `${appConfig.apiUrl}/self-service/deliveryOrder/validate-stops`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useValidateStops() {
    return useMutation({
        mutationKey: [`/self-service/deliveryOrder/validate-stops`],
        mutationFn: async (body: DeliveryValidationRequest) => await validateStops(body),
    });
}

export async function deliveryRideEstimate(
    body: DeliveryArrivalEstimationRequest
): Promise<RideEstimationResponse | null> {
    const response = await axios.post<RideEstimationResponse>(
        `${appConfig.apiUrl}/self-service/deliveryOrder/rideEstimate`,
        body,
        {
            headers: headers(),
        }
    );

    if (response.status === 204) {
        return null;
    }

    return response.data;
}

export function useDeliveryRideEstimate() {
    return useMutation({
        mutationKey: [`/self-service/deliveryOrder/rideEstimate`],
        mutationFn: async (body: DeliveryArrivalEstimationRequest) =>
            await deliveryRideEstimate(body),
    });
}

export async function deliveryOverview(body: DeliveryOverviewRequest): Promise<DeliveryOverview> {
    const response = await axios.post<DeliveryOverview>(
        `${appConfig.apiUrl}/self-service/deliveryOrder/overview`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useDeliveryOverview() {
    return useMutation({
        mutationKey: [`/self-service/deliveryOrder/overview`],
        mutationFn: async (body: DeliveryOverviewRequest) => await deliveryOverview(body),
    });
}

export async function requestMonthlyPayment() {
    const response = await axios.post(
        `${appConfig.apiUrl}/self-service/businesses/request-monthly-payment`,
        null,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useRequestMonthlyPayment() {
    return useMutation({
        mutationKey: [`/self-service/businesses/request-monthly-payment`],
        mutationFn: async () => await requestMonthlyPayment(),
    });
}

export async function createBusiness(
    body: PtyCreateBusinessRequest
): Promise<CreateBusinessResult> {
    const response = await axios.post<CreateBusinessResult>(
        `${appConfig.apiUrl}/self-service/businesses/register`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCreateBusiness() {
    return useMutation({
        mutationKey: [`/self-service/businesses/register`],
        mutationFn: async (body: PtyCreateBusinessRequest) => await createBusiness(body),
    });
}

export async function getUsers(): Promise<BusinessUserDto[]> {
    const response = await axios.get<BusinessUserDto[]>(
        `${appConfig.apiUrl}/self-service/business/users`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetUsers() {
    return useQuery({
        queryKey: [`/self-service/business/users`],
        queryFn: async () => await getUsers(),
    });
}

export async function createUser(body: CreateBusinessUserRequest): Promise<BusinessUserDto[]> {
    const response = await axios.post<BusinessUserDto[]>(
        `${appConfig.apiUrl}/self-service/business/users`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCreateUser() {
    return useMutation({
        mutationKey: [`/self-service/business/users`],
        mutationFn: async (body: CreateBusinessUserRequest) => await createUser(body),
    });
}

export async function getEmployees(): Promise<BusinessEmployeeDto[]> {
    const response = await axios.get<BusinessEmployeeDto[]>(
        `${appConfig.apiUrl}/self-service/business/employees`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetEmployees() {
    return useQuery({
        queryKey: [`/self-service/business/employees`],
        queryFn: async () => await getEmployees(),
    });
}

export async function createEmployee(
    body: CreateOrUpdateEmployeeRequest
): Promise<BusinessEmployeeDto[]> {
    const response = await axios.post<BusinessEmployeeDto[]>(
        `${appConfig.apiUrl}/self-service/business/employees`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCreateEmployee() {
    return useMutation({
        mutationKey: [`/self-service/business/employees`],
        mutationFn: async (body: CreateOrUpdateEmployeeRequest) => await createEmployee(body),
    });
}

export async function executeCardDebt(): Promise<BusinessDebtResponse> {
    const response = await axios.post<BusinessDebtResponse>(
        `${appConfig.apiUrl}/self-service/business/debt/execute`,
        null,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useExecuteCardDebt() {
    return useMutation({
        mutationKey: [`/self-service/business/debt/execute`],
        mutationFn: async () => await executeCardDebt(),
    });
}

export async function initPaymentCardEntry(
    redirectUri: string
): Promise<InitPaymentCardEntryResponse> {
    const response = await axios.post<InitPaymentCardEntryResponse>(
        `${appConfig.apiUrl}/self-service/business/cards/initPaymentCardEntry`,
        null,
        {
            headers: headers(),
            params: {redirectUri},
        }
    );

    return response.data;
}

export function useInitPaymentCardEntry() {
    return useMutation({
        mutationKey: [`/self-service/business/cards/initPaymentCardEntry`],
        mutationFn: async (redirectUri: string) => await initPaymentCardEntry(redirectUri),
    });
}

export async function sendForgottenPasswordEmail(username: string) {
    const response = await axios.post(
        `${appConfig.apiUrl}/self-service/auth/send-forgotten-password-email`,
        null,
        {
            headers: headers(),
            params: {username},
        }
    );

    return response;
}

export function useSendForgottenPasswordEmail() {
    return useMutation({
        mutationKey: [`/self-service/auth/send-forgotten-password-email`],
        mutationFn: async (username: string) => await sendForgottenPasswordEmail(username),
    });
}

export async function login(body: LoginRequest): Promise<LoginResponse> {
    const response = await axios.post<LoginResponse>(
        `${appConfig.apiUrl}/self-service/auth/login`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useLogin() {
    return useMutation({
        mutationKey: [`/self-service/auth/login`],
        mutationFn: async (body: LoginRequest) => await login(body),
    });
}

export async function impersonateBusiness(
    body: ImpersonateBusinessRequest
): Promise<LoginResponse> {
    const response = await axios.post<LoginResponse>(
        `${appConfig.apiUrl}/self-service/auth/impersonate-business`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useImpersonateBusiness() {
    return useMutation({
        mutationKey: [`/self-service/auth/impersonate-business`],
        mutationFn: async (body: ImpersonateBusinessRequest) => await impersonateBusiness(body),
    });
}

export async function changePassword(body: ChangePasswordRequest) {
    const response = await axios.post(
        `${appConfig.apiUrl}/self-service/auth/change-password`,
        body,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useChangePassword() {
    return useMutation({
        mutationKey: [`/self-service/auth/change-password`],
        mutationFn: async (body: ChangePasswordRequest) => await changePassword(body),
    });
}

export async function addOrdererRole(body: AddOrdererRoleRequest): Promise<AddOrdererRoleResponse> {
    const response = await axios.post<AddOrdererRoleResponse>(
        `${appConfig.apiUrl}/self-service/auth/business-admin/add-orderer-role`,
        body,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useAddOrdererRole() {
    return useMutation({
        mutationKey: [`/self-service/auth/business-admin/add-orderer-role`],
        mutationFn: async (body: AddOrdererRoleRequest) => await addOrdererRole(body),
    });
}

export async function getRideUpdates(orderId: string) {
    const response = await axios.get(
        `${appConfig.apiUrl}/self-service/taxiOrder/${encodeURIComponent(orderId)}/updates`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useGetRideUpdates(orderId: string) {
    return useQuery({
        queryKey: [`/self-service/taxiOrder/${encodeURIComponent(orderId)}/updates`],
        queryFn: async () => await getRideUpdates(orderId),
    });
}

export async function getTaxiRideForOrder(orderId: string): Promise<TaxiRide> {
    const response = await axios.get<TaxiRide>(
        `${appConfig.apiUrl}/self-service/taxiOrder/${encodeURIComponent(orderId)}/ride`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetTaxiRideForOrder(orderId: string) {
    return useQuery({
        queryKey: [`/self-service/taxiOrder/${encodeURIComponent(orderId)}/ride`],
        queryFn: async () => await getTaxiRideForOrder(orderId),
    });
}

export async function cancelTaxiRideForOrder(orderId: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/taxiOrder/${encodeURIComponent(orderId)}/ride`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useCancelTaxiRideForOrder() {
    return useMutation({
        mutationKey: [`/self-service/taxiOrder/{orderId}/ride`],
        mutationFn: async (orderId: string) => await cancelTaxiRideForOrder(orderId),
    });
}

export async function publicTaxiOrderConfiguration(
    orderHash?: string
): Promise<PublicTaxiOrderConfiguration> {
    const response = await axios.get<PublicTaxiOrderConfiguration>(
        `${appConfig.apiUrl}/self-service/taxiOrder/public-order/configuration`,
        {
            headers: headers(),
            params: {orderHash},
        }
    );

    return response.data;
}

export function usePublicTaxiOrderConfiguration(orderHash?: string) {
    return useQuery({
        queryKey: [`/self-service/taxiOrder/public-order/configuration`],
        queryFn: async () => await publicTaxiOrderConfiguration(orderHash),
    });
}

export async function driversMapPreview(
    lat: number,
    lon: number
): Promise<TaxiDriversPreviewResponse> {
    const response = await axios.get<TaxiDriversPreviewResponse>(
        `${appConfig.apiUrl}/self-service/taxiOrder/drivers/preview`,
        {
            headers: headers(),
            params: {lat, lon},
        }
    );

    return response.data;
}

export function useDriversMapPreview(lat: number, lon: number) {
    return useQuery({
        queryKey: [`/self-service/taxiOrder/drivers/preview`],
        queryFn: async () => await driversMapPreview(lat, lon),
    });
}

export async function getReceiptPdf(rideId: string) {
    const response = await axios.get(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}/receipt/pdf`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useGetReceiptPdf(rideId: string) {
    return useQuery({
        queryKey: [`/self-service/rides/${encodeURIComponent(rideId)}/receipt/pdf`],
        queryFn: async () => await getReceiptPdf(rideId),
    });
}

export async function getConfirmationPdf(rideId: string) {
    const response = await axios.get(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}/confirmation/pdf`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useGetConfirmationPdf(rideId: string) {
    return useQuery({
        queryKey: [`/self-service/rides/${encodeURIComponent(rideId)}/confirmation/pdf`],
        queryFn: async () => await getConfirmationPdf(rideId),
    });
}

export async function getRideUpdates_1() {
    const response = await axios.get(`${appConfig.apiUrl}/self-service/rides/updates`, {
        headers: headers(),
    });

    return response;
}

export function useGetRideUpdates_1() {
    return useQuery({
        queryKey: [`/self-service/rides/updates`],
        queryFn: async () => await getRideUpdates_1(),
    });
}

export async function getTaxiRides(tierGroup?: string): Promise<TaxiRidesResponse> {
    const response = await axios.get<TaxiRidesResponse>(
        `${appConfig.apiUrl}/self-service/rides/taxi`,
        {
            headers: headers(),
            params: {tierGroup},
        }
    );

    return response.data;
}

export function useGetTaxiRides(tierGroup?: string) {
    return useQuery({
        queryKey: [`/self-service/rides/taxi`],
        queryFn: async () => await getTaxiRides(tierGroup),
    });
}

export async function getDeliveryRides(): Promise<DeliveryRidesResponse> {
    const response = await axios.get<DeliveryRidesResponse>(
        `${appConfig.apiUrl}/self-service/rides/delivery`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetDeliveryRides() {
    return useQuery({
        queryKey: [`/self-service/rides/delivery`],
        queryFn: async () => await getDeliveryRides(),
    });
}

export async function loadInitParams(): Promise<GooglePayInitParamsResponse> {
    const response = await axios.get<GooglePayInitParamsResponse>(
        `${appConfig.apiUrl}/self-service/payment/google/initParams`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useLoadInitParams() {
    return useQuery({
        queryKey: [`/self-service/payment/google/initParams`],
        queryFn: async () => await loadInitParams(),
    });
}

export async function getInitParams(language: string): Promise<ApplePayInitParamsResponse> {
    const response = await axios.get<ApplePayInitParamsResponse>(
        `${appConfig.apiUrl}/self-service/payment/applepay/initParams`,
        {
            headers: headers(),
            params: {language},
        }
    );

    return response.data;
}

export function useGetInitParams(language: string) {
    return useQuery({
        queryKey: [`/self-service/payment/applepay/initParams`],
        queryFn: async () => await getInitParams(language),
    });
}

export async function projectCodeSettings(): Promise<ProjectCodeSettings> {
    const response = await axios.get<ProjectCodeSettings>(
        `${appConfig.apiUrl}/self-service/orderer/projectCodes`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useProjectCodeSettings() {
    return useQuery({
        queryKey: [`/self-service/orderer/projectCodes`],
        queryFn: async () => await projectCodeSettings(),
    });
}

export async function internalNotes(): Promise<InternalNotes> {
    const response = await axios.get<InternalNotes>(
        `${appConfig.apiUrl}/self-service/orderer/internal-notes`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useInternalNotes() {
    return useQuery({
        queryKey: [`/self-service/orderer/internal-notes`],
        queryFn: async () => await internalNotes(),
    });
}

export async function costCenters(): Promise<CostCenters> {
    const response = await axios.get<CostCenters>(
        `${appConfig.apiUrl}/self-service/orderer/costCenters`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useCostCenters() {
    return useQuery({
        queryKey: [`/self-service/orderer/costCenters`],
        queryFn: async () => await costCenters(),
    });
}

export async function coordinatesByLocationId(
    locationId: string,
    suggestSessionId: string
): Promise<Coordinates> {
    const response = await axios.get<Coordinates>(
        `${appConfig.apiUrl}/self-service/location-suggestions/${encodeURIComponent(locationId)}`,
        {
            headers: headers(),
            params: {suggestSessionId},
        }
    );

    return response.data;
}

export function useCoordinatesByLocationId(locationId: string, suggestSessionId: string) {
    return useQuery({
        queryKey: [`/self-service/location-suggestions/${encodeURIComponent(locationId)}`],
        queryFn: async () => await coordinatesByLocationId(locationId, suggestSessionId),
    });
}

export async function callerCompany(): Promise<CompanyInfo> {
    const response = await axios.get<CompanyInfo>(`${appConfig.apiUrl}/self-service/company`, {
        headers: headers(),
    });

    return response.data;
}

export function useCallerCompany() {
    return useQuery({
        queryKey: [`/self-service/company`],
        queryFn: async () => await callerCompany(),
    });
}

export async function employees(): Promise<Employees> {
    const response = await axios.get<Employees>(
        `${appConfig.apiUrl}/self-service/company/employees`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useEmployees() {
    return useQuery({
        queryKey: [`/self-service/company/employees`],
        queryFn: async () => await employees(),
    });
}

export async function companyLookup(companyId: string): Promise<CompanyLookupResponse> {
    const response = await axios.get<CompanyLookupResponse>(
        `${appConfig.apiUrl}/self-service/businesses/lookup`,
        {
            headers: headers(),
            params: {companyId},
        }
    );

    return response.data;
}

export function useCompanyLookup(companyId: string) {
    return useQuery({
        queryKey: [`/self-service/businesses/lookup`],
        queryFn: async () => await companyLookup(companyId),
    });
}

export async function checkEmail(email: string): Promise<CheckEmailResult> {
    const response = await axios.get<CheckEmailResult>(
        `${appConfig.apiUrl}/self-service/businesses/check`,
        {
            headers: headers(),
            params: {email},
        }
    );

    return response.data;
}

export function useCheckEmail(email: string) {
    return useQuery({
        queryKey: [`/self-service/businesses/check`],
        queryFn: async () => await checkEmail(email),
    });
}

export async function isActive(companyId: string): Promise<CopmanyIdActiveResult> {
    const response = await axios.get<CopmanyIdActiveResult>(
        `${appConfig.apiUrl}/self-service/businesses/active`,
        {
            headers: headers(),
            params: {companyId},
        }
    );

    return response.data;
}

export function useIsActive(companyId: string) {
    return useQuery({
        queryKey: [`/self-service/businesses/active`],
        queryFn: async () => await isActive(companyId),
    });
}

export async function getBusinessInvoicings(): Promise<BusinessInvoicingList> {
    const response = await axios.get<BusinessInvoicingList>(
        `${appConfig.apiUrl}/self-service/business/invoicings`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetBusinessInvoicings() {
    return useQuery({
        queryKey: [`/self-service/business/invoicings`],
        queryFn: async () => await getBusinessInvoicings(),
    });
}

export async function getEncodedInvoicingSummary(idInvoicing: number) {
    const response = await axios.get(
        `${appConfig.apiUrl}/self-service/business/invoicings/${encodeURIComponent(idInvoicing)}/summary/encoded`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useGetEncodedInvoicingSummary(idInvoicing: number) {
    return useQuery({
        queryKey: [
            `/self-service/business/invoicings/${encodeURIComponent(idInvoicing)}/summary/encoded`,
        ],
        queryFn: async () => await getEncodedInvoicingSummary(idInvoicing),
    });
}

export async function getEncodedIssuedInvoice(idInvoice: number) {
    const response = await axios.get(
        `${appConfig.apiUrl}/self-service/business/invoices/${encodeURIComponent(idInvoice)}/encoded`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useGetEncodedIssuedInvoice(idInvoice: number) {
    return useQuery({
        queryKey: [`/self-service/business/invoices/${encodeURIComponent(idInvoice)}/encoded`],
        queryFn: async () => await getEncodedIssuedInvoice(idInvoice),
    });
}

export async function getCard(): Promise<CardInfo> {
    const response = await axios.get<CardInfo>(`${appConfig.apiUrl}/self-service/business/cards`, {
        headers: headers(),
    });

    return response.data;
}

export function useGetCard() {
    return useQuery({
        queryKey: [`/self-service/business/cards`],
        queryFn: async () => await getCard(),
    });
}

export async function removeCards() {
    const response = await axios.delete(`${appConfig.apiUrl}/self-service/business/cards`, {
        headers: headers(),
    });

    if (response.status === 204) {
        return null;
    }

    return response;
}

export function useRemoveCards() {
    return useMutation({
        mutationKey: [`/self-service/business/cards`],
        mutationFn: async () => await removeCards(),
    });
}

export async function user(): Promise<UserInfo> {
    const response = await axios.get<UserInfo>(`${appConfig.apiUrl}/self-service/auth/user`, {
        headers: headers(),
    });

    return response.data;
}

export function useUser() {
    return useQuery({queryKey: [`/self-service/auth/user`], queryFn: async () => await user()});
}

export async function getAvailableBusinesses(): Promise<AvailableBusinessesResponse> {
    const response = await axios.get<AvailableBusinessesResponse>(
        `${appConfig.apiUrl}/self-service/auth/available-businesses`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useGetAvailableBusinesses() {
    return useQuery({
        queryKey: [`/self-service/auth/available-businesses`],
        queryFn: async () => await getAvailableBusinesses(),
    });
}

export async function softUpdateNeeded(): Promise<SoftUpdateResponse> {
    const response = await axios.get<SoftUpdateResponse>(
        `${appConfig.apiUrl}/self-service/app/soft-update`,
        {
            headers: headers(),
        }
    );

    return response.data;
}

export function useSoftUpdateNeeded() {
    return useQuery({
        queryKey: [`/self-service/app/soft-update`],
        queryFn: async () => await softUpdateNeeded(),
    });
}

export async function cancelRide(rideId: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useCancelRide() {
    return useMutation({
        mutationKey: [`/self-service/rides/{rideId}`],
        mutationFn: async (rideId: string) => await cancelRide(rideId),
    });
}

export async function cancelStop(rideId: string, stopId: string) {
    const response = await axios.delete(
        `${appConfig.apiUrl}/self-service/rides/${encodeURIComponent(rideId)}/stops/${encodeURIComponent(stopId)}`,
        {
            headers: headers(),
        }
    );

    return response;
}

export function useCancelStop() {
    return useMutation({
        mutationKey: [`/self-service/rides/{rideId}/stops/{stopId}`],
        mutationFn: async ({rideId, stopId}: {rideId: string; stopId: string}) =>
            await cancelStop(rideId, stopId),
    });
}
